import { useCallback } from 'react';

import { useCurrentUser } from '#src/app/shared/hooks/session';
import { getUsState } from '#src/app/shared/utils/unitedStatesMapping';

export type PpmuLocalizedHeadlineData = {
	totalSignatureCount: number;
	userCountryName: string;
	userStateName?: string;
};

type CountryOrStateSignatureCounts =
	| {
			totalSignatureCount: number;
	  }
	| null
	| undefined;

function validateData(userCountryCode: string, userStateCode: string, totalSignatureCount: number) {
	if (!userCountryCode || !userStateCode || totalSignatureCount < 1) {
		return false;
	}
	return true;
}

function getUserStateName(userCountryCode: string, userStateCode: string) {
	if (userCountryCode === 'US' && userStateCode) {
		return getUsState(userStateCode);
	}
	return undefined;
}

function buildHeadlineData(
	countryOrStateSignatureCounts: CountryOrStateSignatureCounts,
	userCountryCode: string,
	userCountryDisplayName: string,
	stateCode: string,
): PpmuLocalizedHeadlineData | undefined {
	const totalSignatureCount = countryOrStateSignatureCounts?.totalSignatureCount ?? 0;
	if (!validateData(userCountryCode, stateCode, totalSignatureCount)) {
		return undefined;
	}
	return {
		totalSignatureCount,
		userCountryName: userCountryDisplayName,
		userStateName: getUserStateName(userCountryCode, stateCode),
	};
}

export function usePpmuLocalizedHeadlineData(): (
	countryOrStateSignatureCounts: CountryOrStateSignatureCounts,
) => PpmuLocalizedHeadlineData | undefined {
	const currentUser = useCurrentUser();

	return useCallback(
		(countryOrStateSignatureCounts: CountryOrStateSignatureCounts) =>
			buildHeadlineData(
				countryOrStateSignatureCounts,
				currentUser?.country?.countryCode ?? '',
				currentUser?.country?.displayName ?? '',
				currentUser?.stateCode ?? '',
			),
		[currentUser],
	);
}
